import React, { useState, useCallback, useMemo } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import "./CustomTable.scss";
import { useNavigate } from "react-router-dom";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import Tab from "../Tabs/Tab";
import PropTypes from "prop-types";
import {
  DataGrid,
  GridToolbarFilterButton,
  gridClasses,
} from "@mui/x-data-grid";
import { alpha, styled } from "@mui/material/styles";

const ODD_OPACITY = 0.2;

// Extracted Toolbar component
const TableToolbar = ({ userFilter, onFilterClick }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "row",
      justifyContent: "end",
      marginTop: "10px",
      marginRight: "10px",
    }}
  >
    {!userFilter ? (
      <button className="btn-filter">
        <GridToolbarFilterButton
          onClick={onFilterClick}
          style={{
            textTransform: "capitalize",
            color: "#2b5ac7",
            fontSize: "14px",
          }}
        />
      </button>
    ) : (
      <div style={{ marginTop: "30px" }}></div>
    )}
  </div>
);

TableToolbar.propTypes = {
  userFilter: PropTypes.bool,
  onFilterClick: PropTypes.func.isRequired,
};

// Helper function to render cell content
const renderCellContent = (column, params) => {
  if (column.id === "userStatus") {
    return (
      <Button variant="contained" className="btn-table">
        <span
          style={{
            fontSize: "14px",
            fontWeight: "500",
            lineHeight: "20px",
            letterSpacing: "0.1px",
            textAlign: "center",
          }}
        >
          {params.value}
        </span>
      </Button>
    );
  }
  
  if (column.format) {
    return column.format(params.value);
  }
  
  return params.value;
};

const CustomTable = ({
  columns,
  data,
  context,
  onRowClick,
  pageNumber,
  rowsPerPage,
  onPageChange,
  totalRecords,
  labels,
  changeTab,
  tableHeight,
  userFilter,
  topPadding,
  tabWidth,
  descendingOrder,
  loading = false
}) => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  
  const handleRowClick = useCallback((rowData) => {
    if (onRowClick) {
      onRowClick(rowData);
    }

    const routes = {
      flagitem: `/flag`,
      user: `/app-users/user/${rowData._id}`,
      gift: `/gifts/gift/${rowData.giftID}`,
      admin: `/admin-users/update-admin/${rowData._id}`,
      moderation: `/moderation/moderation-user/${rowData._id}`
    };

    if (routes[context]) {
      navigate(routes[context], { state: { userData: rowData } });
    }
  }, [context, navigate, onRowClick]);

  const handleFClick = useCallback((_event) => {
    // Filter panel toggle logic can be added here if needed
  }, []);

  const handleSearchChange = useCallback((event) => {
    const { value } = event.target;
    setSearchValue(value);
    onPageChange(1);
  }, [onPageChange]);

  const dataWithIds = useMemo(() => 
    data?.map((row, index) => ({
      ...row,
      id: index + 1,
    })) || [],
    [data]
  );

  const filteredData = useMemo(() => 
    dataWithIds.filter((row) =>
      Object.values(row).some(
        (value) =>
          value?.toString().toLowerCase().includes(searchValue.toLowerCase())
      )
    ),
    [dataWithIds, searchValue]
  );

  const sortedData = useMemo(() => 
    descendingOrder 
      ? [...filteredData].sort((a, b) => b.id - a.id) 
      : filteredData,
    [filteredData, descendingOrder]
  );

  const StripedDataGrid = useMemo(() => 
    styled(DataGrid)(({ theme }) => ({
      [`& .${gridClasses.row}.even`]: {
        backgroundColor: theme.palette.grey[200],
        "&:hover, &.Mui-hovered": {
          backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
          "@media (hover: none)": {
            backgroundColor: "transparent",
          },
        },
        "&.Mui-selected": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
          "&:hover, &.Mui-hovered": {
            backgroundColor: alpha(
              theme.palette.primary.main,
              ODD_OPACITY +
                theme.palette.action.selectedOpacity +
                theme.palette.action.hoverOpacity
            ),
            "@media (hover: none)": {
              backgroundColor: alpha(
                theme.palette.primary.main,
                ODD_OPACITY + theme.palette.action.selectedOpacity
              ),
            },
          },
        },
      },
    })),
    []
  );

  return (
    <Container fluid>
      <Row>
        <Col md={12}>
          <div className="table-container">
            <Form className="mb-3">
              <Form.Group as={Row} controlId="search">
                <Col sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="text"
                    style={{
                      border: "0.01px solid #75777f",
                      borderRadius: "5px",
                    }}
                    placeholder="Search"
                    value={searchValue}
                    onChange={handleSearchChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Col>
                <Col sm={4} md={6}></Col>
              </Form.Group>
            </Form>
            {labels && (
              <Tab labels={labels} changeTab={changeTab} tabWidth={tabWidth} />
            )}
            <div
              style={{
                width: "100%",
                paddingTop: topPadding ? "50px" : "22px",
              }}
            >
              <StripedDataGrid
                style={{
                  height: topPadding ? "700px" : "679px",
                  width: tableHeight ? "40%" : "100%",
                  border: "none",
                  marginTop: "-70px",
                }}
                loading={loading}
                rows={sortedData}
                columns={columns?.map((column) => ({
                  field: column.id,
                  headerName: column.label,
                  flex: 1,
                  minWidth: 210,
                  renderCell: (params) => (
                    <div
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {renderCellContent(column, params)}
                    </div>
                  ),
                }))}
                slots={{
                  toolbar: TableToolbar,
                }}
                slotProps={{
                  toolbar: {
                    userFilter,
                    onFilterClick: handleFClick,
                  },
                }}
                onRowClick={(params) => handleRowClick(params.row)}
                page={pageNumber}
                pageSize={rowsPerPage}
                rowCount={totalRecords}
                onPageChange={onPageChange}
                pageSizeOptions={[10, 20, 30, 50]}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                }
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 10,
                    },
                  },
                }}
                disableColumnMenu
              />
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

CustomTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      format: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  context: PropTypes.string,
  onRowClick: PropTypes.func,
  pageNumber: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  totalRecords: PropTypes.number.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string),
  changeTab: PropTypes.func,
  tableHeight: PropTypes.bool,
  userFilter: PropTypes.bool,
  topPadding: PropTypes.bool,
  tabWidth: PropTypes.string,
  descendingOrder: PropTypes.bool,
  loading: PropTypes.bool,
};

export default CustomTable;
